import { NetworkStatus } from '@apollo/client';
import { ErrorRetry, ListSelectionStyled, SubListSelectionStyled } from '@bechtel/client-common';
import { Box, Checkbox, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { AuthRole } from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { FieldArray, FieldProps } from 'formik';
import { findIndex, get, some } from 'lodash';
import { FC, Fragment } from 'react';
import { useRoleQuery } from '~/user-admin/hooks/use-role';

const RoleList: FC<FieldProps> = ({ form }) => {
  const { loading, error, data, refetch, networkStatus } = useRoleQuery();
  const roleList = get(data, 'roles', []);
  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading Roles. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <Box data-testid="success" flexDirection={{ sm: 'column' }}>
          <SubListSelectionStyled
            onClick={() => {
              if (form.values.roles?.length !== roleList.length) {
                form.setFieldValue('roles', roleList);
              } else {
                form.setFieldValue('roles', []);
              }
            }}
          >
            <Box alignItems={{ sm: 'center' }}>
              <Box mr={{ sm: '500' }}>
                <Checkbox
                  checked={form.values.roles?.length === roleList.length}
                  indeterminate={form.values.roles?.length > 0 && form.values.roles?.length < roleList.length}
                  readOnly
                />
              </Box>
              <Text size="400" weight="600" color="bechtel.grey.1000">
                <Trans>Role</Trans>
              </Text>
            </Box>
            {form.values.roles && form.values.roles.length > 0 && (
              <Text size="400" color="bechtel.grey.1000">
                {form.values.roles.length} <Trans>Selected</Trans>
              </Text>
            )}
          </SubListSelectionStyled>
          <FieldArray name="roles">
            {(arrayHelpers) => {
              return (
                <>
                  {roleList.length > 0 &&
                    roleList.map((role: AuthRole) => {
                      return (
                        <ListSelectionStyled
                          key={role.id}
                          data-testid={`role-list-${role.id}`}
                          onClick={() => {
                            const selected = findIndex(form.values.roles, (item: AuthRole) => {
                              return item.id === role.id;
                            });
                            if (selected !== -1) {
                              arrayHelpers.remove(selected);
                            } else {
                              arrayHelpers.push(role);
                            }
                          }}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Checkbox
                              checked={some(form.values.roles, (item: AuthRole) => {
                                return item.id === role.id;
                              })}
                              readOnly
                            />
                          </Box>
                          <Text size="400" color="bechtel.grey.1000">
                            {role.name}
                          </Text>
                        </ListSelectionStyled>
                      );
                    })}
                </>
              );
            }}
          </FieldArray>
        </Box>
      )}
    </Fragment>
  );
};

export default RoleList;
